import { useQuery } from "@tanstack/react-query";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  Building2, 
  Users, 
  BarChart3, 
  Calendar, 
  Building,
  ChevronDown,
  Plus,
  LogOut,
  Mail,
  Bug,
  FileText,
  CreditCard,
  LayoutGrid,
  Kanban,
  Package2,
  Target,
  Settings
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuAction,
  SidebarFooter,
} from "@/components/ui/sidebar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { useUser } from "@/contexts/UserContext";

type AppRole = 'owner' | 'admin' | 'manager' | 'user';

export function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isNewClientDialogOpen, setIsNewClientDialogOpen] = useState(false);
  const [newClientName, setNewClientName] = useState("");
  const [isBugReportOpen, setIsBugReportOpen] = useState(false);
  
  // Use the UserContext to get user data (now with camelCase)
  const { userId, email, role, companyId, loading } = useUser();

  const { data: company } = useQuery({
    queryKey: ['company', companyId],
    queryFn: async () => {
      if (!companyId) return null;
      
      const { data, error } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!companyId
  });

  const { data: clients, refetch: refetchClients } = useQuery({
    queryKey: ['clients', companyId],
    queryFn: async () => {
      if (!companyId) return [];

      const { data, error } = await supabase
        .from('clients')
        .select(`
          id,
          name,
          projects (
            id,
            name
          )
        `)
        .eq('company_id', companyId);
      
      if (error) throw error;
      return data;
    },
    enabled: !!companyId,
    refetchInterval: 5000
  });

  const handleCreateClient = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newClientName.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Client name is required",
      });
      return;
    }

    if (!companyId) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "No company associated with your profile",
      });
      return;
    }

    const { data: client, error } = await supabase
      .from('clients')
      .insert({
        name: newClientName,
        company_id: companyId,
        billing_details: { currency: 'USD' }
      })
      .select()
      .single();

    if (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create client",
      });
      return;
    }

    setIsNewClientDialogOpen(false);
    setNewClientName("");
    await refetchClients();
    navigate(`/clients/${client.id}`);
    toast({
      title: "Success",
      description: "Client created successfully",
    });
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const isActive = (path: string) => location.pathname === path;
  const userRole = role || 'user';
  
  const canAccessCompany = userRole === 'owner' || userRole === 'admin';
  const canAccessManagement = canAccessCompany || userRole === 'manager';

  return (
    <Sidebar>
      <SidebarHeader className="p-4">
        <h2 className="text-lg font-semibold">Overseer</h2>
        {company?.name && (
          <p className="text-sm text-muted-foreground truncate">{company.name}</p>
        )}
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Management</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {canAccessCompany && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={isActive("/company")}>
                    <Link to="/company">
                      <Building2 className="h-4 w-4" />
                      <span>Company</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              {canAccessManagement && (
                <>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isActive("/employees")}>
                      <Link to="/employees">
                        <Users className="h-4 w-4" />
                        <span>Employees</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isActive("/finances")}>
                      <Link to="/finances">
                        <BarChart3 className="h-4 w-4" />
                        <span>Finances</span>
                      </Link>
                    </SidebarMenuButton>
                    <SidebarMenuSub>
                      <SidebarMenuItem>
                        <SidebarMenuButton asChild isActive={isActive("/invoices")}>
                          <Link to="/invoices">
                            <FileText className="h-4 w-4" />
                            <span>Invoices</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                      <SidebarMenuItem>
                        <SidebarMenuButton asChild isActive={isActive("/transactions")}>
                          <Link to="/transactions">
                            <CreditCard className="h-4 w-4" />
                            <span>Transactions</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    </SidebarMenuSub>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isActive("/assignments2")}>
                      <Link to="/assignments2">
                        <Calendar className="h-4 w-4" />
                        <span>Assignments</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isActive("/inventory")}>
                      <Link to="/inventory">
                        <Package2 className="h-4 w-4" />
                        <span>Inventory</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isActive("/opportunities")}>
                      <Link to="/opportunities">
                        <Target className="h-4 w-4" />
                        <span>Opportunities</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </>
              )}
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={isActive("/board")}>
                  <Link to="/board">
                    <Kanban className="h-4 w-4" />
                    <span>Board</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {canAccessManagement && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={isActive("/settings")}>
                    <Link to="/settings">
                      <Settings className="h-4 w-4" />
                      <span>Settings</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {canAccessManagement && (
          <SidebarGroup>
            <SidebarGroupLabel>
              Clients
              <SidebarMenuAction
                onClick={() => setIsNewClientDialogOpen(true)}
                className="right-2"
                showOnHover={false}
              >
                <Plus className="h-4 w-4" />
              </SidebarMenuAction>
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {clients?.map((client) => (
                  <SidebarMenuItem key={client.id}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive(`/clients/${client.id}`)}
                    >
                      <Link to={`/clients/${client.id}`}>
                        <Building className="h-4 w-4" />
                        <span>{client.name}</span>
                        {client.projects?.length > 0 && (
                          <ChevronDown className="ml-auto h-4 w-4" />
                        )}
                      </Link>
                    </SidebarMenuButton>
                    {client.projects?.length > 0 && (
                      <SidebarMenuSub>
                        {client.projects.map((project) => (
                          <SidebarMenuItem key={project.id}>
                            <SidebarMenuButton
                              asChild
                              size="sm"
                              isActive={isActive(`/projects/${project.id}`)}
                            >
                              <Link to={`/projects/${project.id}`}>
                                {project.name}
                              </Link>
                            </SidebarMenuButton>
                          </SidebarMenuItem>
                        ))}
                      </SidebarMenuSub>
                    )}
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="p-4 space-y-2 mb-16">
        {email && (
          <div className="flex items-center gap-2 px-2 py-1 text-sm text-muted-foreground">
            <Mail className="h-4 w-4" />
            <span className="truncate">{email}</span>
          </div>
        )}
        <Button 
          variant="ghost" 
          className="w-full justify-start" 
          onClick={handleSignOut}
        >
          <LogOut className="mr-2 h-4 w-4" />
          Logout
        </Button>
      </SidebarFooter>

      <Dialog open={isNewClientDialogOpen} onOpenChange={setIsNewClientDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Client</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateClient} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="clientName" className="text-sm font-medium">Client Name</label>
              <Input
                id="clientName"
                value={newClientName}
                onChange={(e) => setNewClientName(e.target.value)}
                placeholder="Enter client name"
                required
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsNewClientDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit">Create Client</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Sidebar>
  );
}
